import { useQuery } from '@tanstack/react-query'
import { Shield, RefreshCw, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api-client'
import type { SslCertificate } from '@/types'

export function SslPage() {
  const { data, refetch } = useQuery({
    queryKey: ['ssl-certificates'],
    queryFn: () => apiClient.get<SslCertificate[]>('/ssl/certificates'),
  })

  const certificates = data?.data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">SSL Certificates</h1>
          <p className="text-muted-foreground">Monitor SSL certificate status and expiration</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh All
        </Button>
      </div>

      {certificates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="mb-2">No SSL certificates</CardTitle>
            <CardDescription>Add domains to automatically monitor SSL certificates</CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {certificates.map((cert) => (
            <Card key={cert.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base font-medium">{cert.subject}</CardTitle>
                {cert.daysUntilExpiry <= 7 ? (
                  <Badge variant="destructive">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    {cert.daysUntilExpiry}d
                  </Badge>
                ) : cert.daysUntilExpiry <= 30 ? (
                  <Badge variant="warning">{cert.daysUntilExpiry} days</Badge>
                ) : (
                  <Badge variant="success">Valid</Badge>
                )}
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p>Issuer: {cert.issuer}</p>
                  <p>Valid from: {new Date(cert.validFrom).toLocaleDateString()}</p>
                  <p>Valid until: {new Date(cert.validTo).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
